import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Popover,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { masterDataService } from '../services/masterData.service';

const ProductMaster = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    department: '',
    category: '',
    subcategory: '',
    criticalLevel: '',
    search: ''
  });

  // Column configuration
  const [columns, setColumns] = useState([
    { id: 'sku', label: 'SKU', visible: true },
    { id: 'description', label: 'Description', visible: true },
    { id: 'department', label: 'Department', visible: true },
    { id: 'category', label: 'Category', visible: true },
    { id: 'subcategory', label: 'Subcategory', visible: true },
    { id: 'product_manufacturer', label: 'Manufacturer', visible: true },
    { id: 'critical_level', label: 'Critical Level', visible: true },
    { id: 'life_cycle_status', label: 'Status', visible: true },
    { id: 'product_brand', label: 'Brand', visible: false },
    { id: 'product_model', label: 'Model', visible: false },
    { id: 'product_size', label: 'Size', visible: false },
    { id: 'temperature_zone', label: 'Temp Zone', visible: false },
    { id: 'case_pack', label: 'Case Pack', visible: false },
    { id: 'unit_cost', label: 'Unit Cost', visible: false },
    { id: 'shelf_life_days', label: 'Shelf Life', visible: false }
  ]);

  // Column visibility popover state
  const [anchorEl, setAnchorEl] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const result = await masterDataService.getProductsWithAttributes();
        if (result.status === 'success') {
          setProducts(result.data);
          setFilteredProducts(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch products');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...products];
    
    if (filters.department) {
      filtered = filtered.filter(p => p.department === filters.department);
    }
    if (filters.category) {
      filtered = filtered.filter(p => p.category === filters.category);
    }
    if (filters.subcategory) {
      filtered = filtered.filter(p => p.subcategory === filters.subcategory);
    }
    if (filters.criticalLevel) {
      filtered = filtered.filter(p => 
        p.extended_attributes?.critical_level?.value === filters.criticalLevel
      );
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p => 
        p.sku.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower) ||
        p.product_manufacturer?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredProducts(filtered);
    setPage(0); // Reset to first page when filters change
  }, [filters, products]);

  // Get unique values for filter dropdowns
  const departments = [...new Set(products.map(p => p.department).filter(Boolean))];
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
  const subcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))];
  const criticalLevels = ['A', 'B', 'C', 'D'];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Column visibility handlers
  const handleColumnVisibilityClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnVisibilityClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnId) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const getCriticalLevelColor = (level) => {
    switch (level) {
      case 'A': return 'error';
      case 'B': return 'warning';
      case 'C': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Product Master
        </Typography>
        <IconButton onClick={handleColumnVisibilityClick}>
          <ViewColumnIcon />
        </IconButton>
      </Box>

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleColumnVisibilityClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Visible Columns
          </Typography>
          <FormGroup>
            {columns.map((column) => (
              <FormControlLabel
                key={column.id}
                control={
                  <Checkbox
                    checked={column.visible}
                    onChange={() => handleColumnToggle(column.id)}
                  />
                }
                label={column.label}
              />
            ))}
          </FormGroup>
          <Button 
            variant="contained" 
            onClick={handleColumnVisibilityClose}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Popover>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={filters.department}
              label="Department"
              onChange={(e) => handleFilterChange('department', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {departments.map(dept => (
                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Subcategory</InputLabel>
            <Select
              value={filters.subcategory}
              label="Subcategory"
              onChange={(e) => handleFilterChange('subcategory', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {subcategories.map(subcat => (
                <MenuItem key={subcat} value={subcat}>{subcat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Critical Level</InputLabel>
            <Select
              value={filters.criticalLevel}
              label="Critical Level"
              onChange={(e) => handleFilterChange('criticalLevel', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {criticalLevels.map(level => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Search"
            variant="outlined"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 200 }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.filter(col => col.visible).map((column) => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((product) => (
                <TableRow key={product.product_id}>
                  {columns.filter(col => col.visible).map((column) => {
                    if (column.id === 'critical_level') {
                      return (
                        <TableCell key={column.id}>
                          {product.extended_attributes?.critical_level?.value && (
                            <Chip 
                              label={product.extended_attributes.critical_level.value}
                              color={getCriticalLevelColor(product.extended_attributes.critical_level.value)}
                            />
                          )}
                        </TableCell>
                      );
                    }
                    if (column.id === 'life_cycle_status') {
                      return (
                        <TableCell key={column.id}>
                          <Chip 
                            label={product.life_cycle_status}
                            color={product.life_cycle_status === 'ACTIVE' ? 'success' : 'default'}
                          />
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={column.id}>
                        {product[column.id]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </div>
  );
};

export default ProductMaster;
